import { useQuery } from "@tanstack/react-query";
import { useOrg360Api } from "./useOrg360Api";
import type { Org360IncludeFlags } from "./useOrg360GraphData";

export interface Org360ImpactItem {
  id: string;
  label: string;
  type: string;
  rel: string;
  depth: number;
}

export interface Org360ImpactResult {
  items: Org360ImpactItem[];
  found: boolean;
  maxDepth: number;
  truncated: boolean;
  byRel: Record<string, number>;
}

interface ImpactNode {
  key: string;
  label: string;
  type: string;
  rel: string | null;
  depth: number;
  children?: ImpactNode[];
}

interface ImpactPayload {
  found: boolean;
  tree: ImpactNode | null;
  stats?: {
    max_depth?: number;
    truncated?: boolean;
    depth_limited?: boolean;
    by_rel?: Record<string, number>;
  };
}

function flatten(tree: ImpactNode | null): Org360ImpactItem[] {
  const out: Org360ImpactItem[] = [];
  const walk = (node: ImpactNode) => {
    for (const c of node.children ?? []) {
      out.push({ id: c.key, label: c.label, type: c.type, rel: c.rel ?? "", depth: c.depth });
      walk(c);
    }
  };
  if (tree) walk(tree);
  return out;
}

/**
 * Transitive blast radius from the Neo4j-backed /api/impact engine. Replaces the
 * client-side BFS over the degree-pruned graph payload, which returned nothing
 * for nodes that weren't in the pruned default graph (e.g. flows).
 * direction: "in" = dependents (what breaks if changed), "out" = dependencies.
 */
export function useOrg360Impact(
  nodeKey: string | null,
  direction: "in" | "out",
  depth: number,
  include?: Org360IncludeFlags,
) {
  const { getJson, orgId } = useOrg360Api();
  const inc = {
    fields: !!include?.fields,
    reports: !!include?.reports,
    managed: !!include?.managed,
  };

  return useQuery<Org360ImpactResult>({
    queryKey: ["org360-impact", orgId, nodeKey, direction, depth, inc.fields, inc.reports, inc.managed],
    queryFn: async () => {
      const params: Record<string, string | undefined> = {
        org_id: orgId,
        key: nodeKey ?? "",
        direction,
        depth: String(depth),
      };
      if (inc.fields) params.include_fields = "1";
      if (inc.reports) params.include_reports = "1";
      if (inc.managed) params.include_managed = "1";
      const res = await getJson<{ ok: boolean; data: ImpactPayload }>("/api/impact", params);
      const d = res?.data;
      return {
        items: flatten(d?.tree ?? null),
        found: !!d?.found,
        maxDepth: d?.stats?.max_depth ?? 0,
        truncated: !!(d?.stats?.truncated || d?.stats?.depth_limited),
        byRel: d?.stats?.by_rel ?? {},
      };
    },
    enabled: !!orgId && !!nodeKey,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
